import { gql, useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useResolve } from "react-jpex";
import { z } from "zod";

import type { TokenResult } from "typings/TokenResult";

import { AuthenticationService } from "typings/AuthenticationService";
import { UserTopics } from "typings/Topics";

type LoginFormData = {
  email: string;
  password: string;
};

const schema = z.object({
  email: z.string().trim().email(),
  password: z.string().min(1),
});

const LOGIN = gql`
  mutation Login($email: String!, $password: String!) {
    result: login(email: $email, password: $password) {
      accessToken
      refreshToken
    }
  }
`;

export const useLogin = () => {
  const authenticationService = useResolve<AuthenticationService>(
    "AuthenticationService",
  );
  const form = useForm<LoginFormData>({
    mode: "onChange",
    resolver: zodResolver(schema),
  });

  const [mutate, { client, error, loading }] = useMutation<TokenResult>(LOGIN);

  async function handleSubmit(formData: LoginFormData) {
    authenticationService.setAccessToken(undefined);
    authenticationService.setRefreshToken(undefined);

    const { data } = await mutate({
      variables: { ...formData },
    });

    if (data?.result) {
      await client.clearStore();

      authenticationService.setAccessToken(data.result.accessToken);
      authenticationService.setRefreshToken(data.result.refreshToken);

      PubSub.publish(
        authenticationService.isMfaRequired()
          ? UserTopics.MfaRequired
          : UserTopics.LoggedIn,
      );
    }
  }

  return {
    form,
    handleSubmit: form.handleSubmit(handleSubmit),
    state: { error, loading },
  };
};
